import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { getPrivacyChoice, setPrivacyChoice } from "@/lib/privacyConsent";
import { isNative } from "@/lib/native";

export default function PrivacyConsent() {
  const [choice, setChoice] = useState(() => getPrivacyChoice());
  const [manage, setManage] = useState(false);
  const [analytics, setAnalytics] = useState(choice === "optional");

  useEffect(() => {
    const handler = (event) => {
      const next = event?.detail || getPrivacyChoice();
      setChoice(next);
      setAnalytics(next === "optional");
    };
    window.addEventListener("un-privacy-choice", handler);
    const open = () => setManage(true);
    window.addEventListener("un-open-privacy", open);
    return () => { window.removeEventListener("un-privacy-choice", handler); window.removeEventListener("un-open-privacy", open); };
  }, []);

  const native = isNative();

  const choose = (next) => {
    setPrivacyChoice(next);
    setChoice(next);
    setManage(false);
  };

  return (
    <>
      <AnimatePresence>
        {!native && !choice && !manage && (
          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            className="fixed z-[100] left-3 right-3 bottom-3 max-w-[680px] mx-auto rounded-[1.4rem] p-4 sm:p-5"
            style={{ background: "rgba(42,27,42,0.97)", color: "#fffaf5", boxShadow: "0 22px 70px rgba(0,0,0,.25)", backdropFilter: "blur(16px)" }}
            aria-label="Privacy choices"
          >
            <p className="text-[17px]" style={{ fontFamily: "Lora, serif" }}>Your privacy, your choice</p>
            <p className="mt-1 text-[11px] leading-relaxed" style={{ color: "rgba(255,250,245,.72)" }}>
              Essential storage keeps UnAnonymous working. Optional product usage signals tied to a random installation ID can help improve the product. Private reflections are never used for advertising.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <button onClick={() => choose("optional")} className="px-3.5 py-2 rounded-full text-[11px] font-medium" style={{ background: "#fffaf5", color: "var(--un-plum-deep)" }}>Accept optional</button>
              <button onClick={() => choose("essential")} className="px-3.5 py-2 rounded-full text-[11px]" style={{ border: "1px solid rgba(255,255,255,.16)" }}>Essential only</button>
              <button onClick={() => setManage(true)} className="px-3.5 py-2 rounded-full text-[11px] underline underline-offset-4">Manage privacy</button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {manage && (
          <motion.div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-3" style={{ background: "rgba(42,27,42,.28)" }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={(e) => e.target === e.currentTarget && setManage(false)}>
            <motion.section initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }} className="w-full max-w-[520px] rounded-[1.75rem] p-5" style={{ background: "#fffaf5", boxShadow: "0 24px 90px rgba(0,0,0,.22)" }}>
              <p className="text-[10px] uppercase tracking-[.28em]" style={{ color: "var(--un-text-3)" }}>Privacy choices</p>
              <h2 className="mt-1 text-[28px] leading-tight" style={{ fontFamily: "Lora, serif", color: "var(--un-plum-deep)" }}>Choose what helps the site work for you.</h2>
              <p className="mt-2 text-[12px] leading-relaxed" style={{ color: "var(--un-text-2)" }}>The default is minimal. UnAnonymous does not use advertising trackers or private reflection content for targeting.</p>

              <div className="mt-5 py-4 flex items-center justify-between gap-4" style={{ borderTop: "1px solid rgba(91,57,84,.09)" }}>
                <div><p className="text-[14px] font-medium">Essential</p><p className="text-[11px]" style={{ color: "var(--un-text-3)" }}>Security, session state, preferences and core site functions.</p></div>
                <span className="px-2.5 py-1 rounded-full text-[10px]" style={{ background: "rgba(74,107,93,.09)", color: "var(--un-safe)" }}>Always on</span>
              </div>
              <div className="py-4 flex items-center justify-between gap-4" style={{ borderTop: "1px solid rgba(91,57,84,.09)" }}>
                <div><p className="text-[14px] font-medium">Optional analytics</p><p className="text-[11px]" style={{ color: "var(--un-text-3)" }}>Limited product events tied to a random installation ID; never journal or reflection text.</p></div>
                <button role="switch" aria-checked={analytics} onClick={() => setAnalytics((v) => !v)} className="relative w-12 h-7 rounded-full" style={{ background: analytics ? "var(--un-plum)" : "rgba(91,57,84,.16)" }}>
                  <span className="absolute top-1 w-5 h-5 rounded-full transition-all" style={{ left: analytics ? 24 : 4, background: "#fffaf5" }} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button onClick={() => setManage(false)} className="py-3 rounded-2xl text-[13px]" style={{ border: "1px solid rgba(91,57,84,.12)", color: "var(--un-plum)" }}>Cancel</button>
                <button onClick={() => choose(analytics ? "optional" : "essential")} className="py-3 rounded-2xl text-[13px] font-medium" style={{ background: "var(--un-plum)", color: "#fffaf5" }}>Save choices</button>
              </div>
            </motion.section>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
